'use client';
import React, { Suspense, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';

import { SignUp } from '@/components/Auth/SignUp';
import { useSearchParams } from 'next/navigation';
import { storeUTMInLocalStorage } from '@/utils/utmTracking';
import { stashPendingPromo } from '@/lib/partnerPromo';

const ROUTES = {
  HOME: '/home',
  ONBOARDING: '/onboarding',
  SIGNUP: '/signup',
  LOGIN: '/login',
  EXTENSION_LOGIN: '/extension/login',
} as const;

const QUERY_PARAMS = {
  CALLBACK_URL: 'callbackURLAfterSignUp',
  EXTENSION_NAME: 'extensionName',
  SIGNUP: 'signup',
} as const;

const SignupContent = () => {
  const params = useSearchParams();
  const extensionName = params?.get(QUERY_PARAMS.EXTENSION_NAME);
  const callbackURL = params?.get(QUERY_PARAMS.CALLBACK_URL) || undefined;
  const isSignup = params?.get(QUERY_PARAMS.SIGNUP);
  const isCli = params?.get('cli');
  const cliRedirect = params?.get('cli_redirect');
  const cliState = params?.get('cli_state');
  const promo = params?.get('promo');

  useEffect(() => {
    storeUTMInLocalStorage();
    // Partner referral (e.g. Fireworks) — note it synchronously so it survives
    // the OAuth round-trip. Not verified here on purpose: the provider buttons
    // are clickable the moment this renders, and a referral that waits on a
    // fetch is a referral we lose to a fast click. Onboarding verifies it before
    // it can gate anything.
    if (promo) {
      stashPendingPromo(promo);
    }
    if (isCli === 'true') {
      localStorage.setItem('cli_signup_flow', 'true');
      // Preserve the CLI loopback params through signup -> login -> /cli-auth.
      if (cliRedirect) {
        localStorage.setItem('cli_redirect', cliRedirect);
      }
      if (cliState) {
        localStorage.setItem('cli_state', cliState);
      }
    }
  }, [isCli, cliRedirect, cliState, promo]);

  let finalCallbackURL = callbackURL;
  if (extensionName) {
    // If it's an extension signup, set callback to extension login
    finalCallbackURL = `${ROUTES.EXTENSION_LOGIN}?${QUERY_PARAMS.EXTENSION_NAME}=${extensionName}`;
  } else if (isCli === 'true') {
    // Thread the CLI params through the OAuth callback so a new-account signup
    // lands back on /login with them intact (not just in localStorage), where
    // redirectCliFlow() forwards to /cli-auth. Without this, an OAuth callback
    // that bypasses /login would abandon the CLI flow.
    const cliParams = new URLSearchParams({ cli: 'true' });
    if (cliRedirect) {
      cliParams.set('cli_redirect', cliRedirect);
    }
    if (cliState) {
      cliParams.set('cli_state', cliState);
    }
    finalCallbackURL = `${ROUTES.LOGIN}?${cliParams.toString()}`;
  } else if (callbackURL && isSignup) {
    finalCallbackURL = `${callbackURL}?${QUERY_PARAMS.SIGNUP}=${isSignup}`;
  }

  return (
    <div className="h-screen bg-ds-brand-darker p-[20px] flex items-stretch">
      <Suspense
        fallback={
          <Loader2
            className="h-8 w-8 animate-spin text-brand-subtle mx-auto"
            aria-hidden
          />
        }
      >
        <AnimatePresence mode="wait">
          <SignUp key="signup" callbackURL={finalCallbackURL} />
        </AnimatePresence>
      </Suspense>
    </div>
  );
};

const SignupPage = () => (
  <Suspense fallback={<div className="animate-pulse h-64" />}>
    <SignupContent />
  </Suspense>
);

export default SignupPage;
